/**
 * postPayRunToLedger (PR6)
 * ────────────────────────
 * Bridges a sanctioned `PayRunRecord` into the canonical expense ledger.
 *
 * Architecture:
 *   • REMOTE (when Lovable Cloud is configured) — invokes the
 *     `expense-ledger-write` Edge Function once per ledger line. The
 *     Edge Function enforces role + category gates server-side and is
 *     the only path that writes to the real `expense_ledger` table.
 *   • LOCAL MIRROR — regardless of remote success, appends mirrored
 *     entries to the in-memory `ExpenseLedgerContext` so every read
 *     surface (Job Cost Breakdown, `/accounting`) reflects the release
 *     immediately during beta. The mirror IDs are stable and prefixed
 *     so a future reconciliation sweep can dedupe against remote rows.
 *
 * Hard rules:
 *   • Never writes `sales_commission` — that lane is owned by the
 *     commission engine. Any such line in the record is dropped from
 *     the post and surfaced in `skipped[]`.
 *   • Append-only. If remote write fails we DO NOT roll back the
 *     PayRunRecord — the record is authoritative; ledger posting will
 *     retry from the cockpit. We surface per-line `posted | local_only | error`.
 *   • Idempotent on the local mirror: dedup by entry id.
 */

import { supabase } from "@/lib/supabase";
const isSupabaseConfigured = !!import.meta.env.VITE_SUPABASE_URL;
import {
  payRunLedgerPayloads,
  type PayRunRecord,
} from "./recordPayRun";
import type { ExpenseLedgerEntry } from "@/data/jobCost/expenseLedgerBridge";

export interface PostPayRunToLedgerOptions {
  /** Required for remote post. When absent, posts local-mirror only. */
  organizationId?: string;
  /**
   * Optional override of the date the entry counts as occurring on
   * (YYYY-MM-DD). Defaults to the release date on the record.
   */
  occurredOn?: string;
}

export type LedgerLinePostStatus =
  | "posted"        // remote write succeeded; local mirror appended
  | "local_only"    // remote unavailable or org missing; local mirror appended
  | "skipped"       // category blocked (e.g. sales_commission)
  | "error";        // remote returned an error; local mirror still appended

export interface LedgerLinePostResult {
  index: number;
  status: LedgerLinePostStatus;
  remoteId?: string;
  error?: string;
  category: string;
  amountCents: number;
}

export interface PostPayRunToLedgerResult {
  payRunId: string;
  lineResults: LedgerLinePostResult[];
  mirrored: ExpenseLedgerEntry[];
  remoteAttempted: boolean;
  /** True when at least one line failed at the remote endpoint. */
  hadErrors: boolean;
}

const BLOCKED_CATEGORIES = new Set(["sales_commission"]);

function makeMirrorId(payRunId: string, index: number): string {
  return `mir-payrun-${payRunId}-${index}`;
}

export async function postPayRunToLedger(
  record: PayRunRecord,
  options: PostPayRunToLedgerOptions = {},
): Promise<PostPayRunToLedgerResult> {
  const payloads = payRunLedgerPayloads(record);
  const remoteAttempted = isSupabaseConfigured && !!options.organizationId;
  const lineResults: LedgerLinePostResult[] = [];
  const mirrored: ExpenseLedgerEntry[] = [];
  let hadErrors = false;

  for (let i = 0; i < payloads.length; i++) {
    const payload = payloads[i];
    const occurredOn = options.occurredOn ?? payload.occurredOn;
    const base = {
      index: i,
      category: payload.category,
      amountCents: payload.amountCents,
    };

    // Hard guard: never post sales_commission through this lane.
    if (BLOCKED_CATEGORIES.has(payload.category)) {
      lineResults.push({ ...base, status: "skipped", error: "category_blocked" });
      continue;
    }

    // Local mirror — always append, even when remote will run.
    const mirror: ExpenseLedgerEntry = {
      id: makeMirrorId(record.id, i),
      jobId: payload.jobId,
      category: payload.category,
      source: "manual_entry",
      amountCents: payload.amountCents,
      occurredOn,
      memo: payload.memo,
      referenceId: payload.referenceId ?? record.id,
      createdAt: new Date().toISOString(),
    };
    mirrored.push(mirror);

    // Remote post — best-effort.
    if (!remoteAttempted) {
      lineResults.push({ ...base, status: "local_only" });
      continue;
    }

    try {
      const { data, error } = await supabase.functions.invoke<{
        id?: string;
        error?: string;
      }>("expense-ledger-write", {
        body: {
          organizationId: options.organizationId,
          jobId: payload.jobId,
          category: payload.category,
          amountCents: payload.amountCents,
          occurredOn,
          memo: payload.memo,
          referenceId: payload.referenceId ?? record.id,
        },
      });

      if (error || data?.error || !data?.id) {
        hadErrors = true;
        lineResults.push({
          ...base,
          status: "error",
          error: error?.message ?? data?.error ?? "unknown_error",
        });
      } else {
        lineResults.push({ ...base, status: "posted", remoteId: data.id });
      }
    } catch (e) {
      hadErrors = true;
      lineResults.push({
        ...base,
        status: "error",
        error: e instanceof Error ? e.message : "invoke_failed",
      });
    }
  }

  return { payRunId: record.id, lineResults, mirrored, remoteAttempted, hadErrors };
}

/** Compact UX-friendly summary for toasts. */
export function summarizeLedgerPost(result: PostPayRunToLedgerResult): string {
  const posted = result.lineResults.filter((l) => l.status === "posted").length;
  const local = result.lineResults.filter((l) => l.status === "local_only").length;
  const errored = result.lineResults.filter((l) => l.status === "error").length;
  const skipped = result.lineResults.filter((l) => l.status === "skipped").length;
  const parts: string[] = [];
  if (posted) parts.push(`${posted} posted`);
  if (local) parts.push(`${local} local-only`);
  if (errored) parts.push(`${errored} errored`);
  if (skipped) parts.push(`${skipped} skipped`);
  return parts.join(" · ") || "no lines";
}
